// src/components/parent-dashboard/api/preparatoryApiService.js

/**
 * API Service for Preparatory Stage (Grades 3-5) Parent Dashboard
 * Contains specialized endpoints for preparatory stage
 * Matching the backend FastAPI endpoints in parent_preparatory.py
 */

import { API_BASE_URL } from "../../../config";

// Enum values matching the backend
const ParentResponse = {
    YES: "Yes",
    SOMETIMES: "Sometimes",
    NO: "No",
    NOT_SURE: "Not sure"
};

const HomeResource = {
    BOOKS_MAGAZINES: "Books and Magazines",
    NEWSPAPERS: "Newspapers",
    TOYS_GAMES_SPORTS: "Toys, Games and sports",
    PHONE_COMPUTER: "Phone and Computer",
    INTERNET: "Internet",
    PUBLIC_BROADCAST: "Public Broadcast System",
    CWSN_RESOURCES: "Resources for CWSN"
};

const SupportArea = {
    ORAL_COMMUNICATION: "Oral communication (R1 or R2)",
    READING: "Reading",
    NUMBERS_AND_MATH: "Numbers and Math",
    SELF_CONFIDENCE: "Self-confidence",
    WORKING_WITH_CHILDREN: "Working with other children",
    WORKING_INDEPENDENTLY: "Working independently at home",
    OTHER_SUBJECT_AREAS: "Other subject areas"
};

// Helper function to get auth headers
const getAuthHeaders = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
        throw new Error("No access token found. Please log in again.");
    }

    return {
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json"
    };
};

// Helper function to handle API responses
const handleApiResponse = async (response) => {
    const responseData = await response.json();

    if (!response.ok) {
        // Handle different error status codes
        if (response.status === 401) {
            throw new Error("Authentication failed. Please log in again.");
        } else if (response.status === 403) {
            throw new Error("Access denied. You don't have permission to perform this action.");
        } else if (response.status === 404) {
            throw new Error("Resource not found. Please check if the assignment exists.");
        } else if (response.status >= 500) {
            throw new Error("Server error. Please try again later.");
        } else {
            throw new Error(responseData.detail || responseData.message || "An error occurred");
        }
    }

    return responseData;
};

const preparatoryApiService = {
    // Get child's submission for an assignment - Matches backend endpoint
    getChildSubmission: async (childId, assignmentId) => {
        try {
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            const response = await fetch(
                `${API_BASE_URL}/preparatory/parent/children/${childId}/assignments/${assignmentId}/submission`,
                {
                    method: "GET",
                    headers: getAuthHeaders()
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error fetching child's submission:", error);
            throw error;
        }
    },

    // Add caregiver observation - Matches backend endpoint and data structure
    addCaregiverObservation: async (childId, assignmentId, formData) => {
        try {
            // Validation
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            // Convert from UI form format to backend expected format
            const payload = {
                // Screen 1: home resources
                resources: Array.isArray(formData.resources) ? formData.resources : [],
                other_resources: formData.other_resources?.trim() || "",

                // Screen 2: parent assessment - ensure all required fields are present
                assessment: {
                    finds_school_safe: formData.assessment?.finds_school_safe || ParentResponse.NOT_SURE,
                    participates_in_activities: formData.assessment?.participates_in_activities || ParentResponse.NOT_SURE,
                    finds_curriculum_difficult: formData.assessment?.finds_curriculum_difficult || ParentResponse.NOT_SURE,
                    making_good_progress: formData.assessment?.making_good_progress || ParentResponse.NOT_SURE,
                    gets_school_support: formData.assessment?.gets_school_support || ParentResponse.NOT_SURE,
                    can_talk_feelings: formData.assessment?.can_talk_feelings || ParentResponse.NOT_SURE,
                    can_calm_down: formData.assessment?.can_calm_down || ParentResponse.NOT_SURE,
                    can_understand_friends: formData.assessment?.can_understand_friends || ParentResponse.NOT_SURE,
                    respects_opinions: formData.assessment?.respects_opinions || ParentResponse.NOT_SURE,
                    helps_after_fight: formData.assessment?.helps_after_fight || ParentResponse.NOT_SURE,
                    comforts_when_sad: formData.assessment?.comforts_when_sad || ParentResponse.NOT_SURE
                },

                // Support needs
                needs_support: Array.isArray(formData.needs_support) ? formData.needs_support : [],
                other_support: formData.other_support?.trim() || ""
            };

            console.log("Sending payload to API:", payload);

            const response = await fetch(
                `${API_BASE_URL}/preparatory/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-observation`,
                {
                    method: "POST",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            const result = await handleApiResponse(response);
            console.log("API response:", result);

            return result;
        } catch (error) {
            console.error("Error adding caregiver observation:", error);
            throw error;
        }
    },

    // Optional: Get all assignments for a child
    getChildAssignments: async (childId) => {
        try {
            if (!childId) {
                throw new Error("Child ID is required");
            }

            const response = await fetch(
                `${API_BASE_URL}/preparatory/parent/children/${childId}/assignments`,
                {
                    method: "GET",
                    headers: getAuthHeaders()
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error fetching child's assignments:", error);
            throw error;
        }
    },

    // Optional: Update an existing caregiver observation
    updateCaregiverObservation: async (childId, assignmentId, formData) => {
        try {
            if (!childId || !assignmentId) {
                throw new Error("Child ID and Assignment ID are required");
            }

            if (!formData || typeof formData !== 'object') {
                throw new Error("Form data is required");
            }

            const payload = {
                resources: Array.isArray(formData.resources) ? formData.resources : [],
                other_resources: formData.other_resources?.trim() || "",
                assessment: {
                    finds_school_safe: formData.assessment?.finds_school_safe || ParentResponse.NOT_SURE,
                    participates_in_activities: formData.assessment?.participates_in_activities || ParentResponse.NOT_SURE,
                    finds_curriculum_difficult: formData.assessment?.finds_curriculum_difficult || ParentResponse.NOT_SURE,
                    making_good_progress: formData.assessment?.making_good_progress || ParentResponse.NOT_SURE,
                    gets_school_support: formData.assessment?.gets_school_support || ParentResponse.NOT_SURE,
                    can_talk_feelings: formData.assessment?.can_talk_feelings || ParentResponse.NOT_SURE,
                    can_calm_down: formData.assessment?.can_calm_down || ParentResponse.NOT_SURE,
                    can_understand_friends: formData.assessment?.can_understand_friends || ParentResponse.NOT_SURE,
                    respects_opinions: formData.assessment?.respects_opinions || ParentResponse.NOT_SURE,
                    helps_after_fight: formData.assessment?.helps_after_fight || ParentResponse.NOT_SURE,
                    comforts_when_sad: formData.assessment?.comforts_when_sad || ParentResponse.NOT_SURE
                },
                needs_support: Array.isArray(formData.needs_support) ? formData.needs_support : [],
                other_support: formData.other_support?.trim() || ""
            };

            const response = await fetch(
                `${API_BASE_URL}/preparatory/parent/children/${childId}/assignments/${assignmentId}/submission/caregiver-observation`,
                {
                    method: "PUT",
                    headers: getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            return await handleApiResponse(response);
        } catch (error) {
            console.error("Error updating caregiver observation:", error);
            throw error;
        }
    },

    // Export the enum values for use in components
    ParentResponse,
    HomeResource,
    SupportArea
};

export default preparatoryApiService;